'use client';

import { ComponentType, ReactNode, useEffect, useState } from 'react';

type Loader<T extends object> = () => Promise<{ default: ComponentType<T> } | ComponentType<T>>;

type ClientOnlyLoaderProps<T extends object> = {
  load: Loader<T>;
  fallback?: ReactNode;
  props?: T;
};

// Renders a server-safe fallback first, then hydrates with the client-only component once mounted.
export function ClientOnlyLoader<T extends object = Record<string, never>>({
  load,
  fallback = null,
  props,
}: ClientOnlyLoaderProps<T>) {
  const [ClientComponent, setClientComponent] = useState<ComponentType<T> | null>(null);

  useEffect(() => {
    let active = true;
    Promise.resolve(load()).then((mod) => {
      const Resolved = (mod as any).default ?? mod;
      if (active) {
        setClientComponent(() => Resolved as ComponentType<T>);
      }
    });
    return () => {
      active = false;
    };
  }, [load]);

  if (!ClientComponent) {
    return <>{fallback}</>;
  }

  const componentProps = (props ?? {}) as T;
  return <ClientComponent {...componentProps} />;
}
